import { memo, useMemo } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useEditorContext } from 'components/editor';
import { EditorImport } from 'components/editor/import';
import {
  EditorHeaderActions,
  EditorHeaderChapter,
  EditorHeaderSelectedBlocks
} from '.';

import type { GetCourseTranslationProgressRes } from 'shared-types/responses';
import type { Editor } from 'types';

export type EditorHeaderStateProps = {
  activeLanguageVersion: Editor['editorActiveLanguageVersion'];
  chapters: Editor['editorChapters'];
  currentChapter: Editor['editorCurrentChapter'];
  importVisible: Editor['editorImportVisible'];
  isCopyingBlocks: Editor['editorCopyBlocksLoading'];
  isCopyingBlocksSuccess: Editor['editorCopyBlocksSuccess'];
  isDisabled: Editor['editorIsDisabled'];
  isDiscarding: Editor['editorIsDiscarding'];
  isDraft: Editor['editorIsDraft'];
  isImporting: Editor['editorIsImporting'];
  isMoving: Editor['editorIsBlockMoveView'];
  isPublishing: Editor['editorIsPublishing'];
  isSaving: Editor['editorIsSaving'];
  languageVersions: Editor['editorPreviewVersionCourseLanguageVersions'];
  previewType: Editor['editorPreviewType'];
  previewVersionCourseLanguage: Editor['editorPreviewVersionCourseLanguage'];
  previewVersionCourseLanguageVersions: Editor['editorPreviewVersionCourseLanguageVersions'];
  publishDate: Editor['editorPublishDate'];
  status: Editor['editorHeaderStatus'];
  translationReferenceLanguage: Editor['editorTranslationReferenceLanguage'];
  updatedAt: Editor['editorHeaderUpdatedAt'];
};

interface Props {
  translationProgressData: GetCourseTranslationProgressRes;
  updateTranslationProgressData: () => void;
}

const EditorHeader = ({
  translationProgressData,
  updateTranslationProgressData
}: Props) => {
  const state = useEditorContext();

  const stateProps: EditorHeaderStateProps = {
    activeLanguageVersion: state.editorActiveLanguageVersion,
    chapters: state.editorChapters,
    currentChapter: state.editorCurrentChapter,
    importVisible: state.editorImportVisible,
    isCopyingBlocks: state.editorCopyBlocksLoading,
    isCopyingBlocksSuccess: state.editorCopyBlocksSuccess,
    isDisabled: state.editorIsDisabled,
    isDiscarding: state.editorIsDiscarding,
    isDraft: state.editorIsDraft,
    isImporting: state.editorIsImporting,
    isMoving: state.editorIsBlockMoveView,
    isPublishing: state.editorIsPublishing,
    isSaving: state.editorIsSaving,
    languageVersions: state.editorPreviewVersionCourseLanguageVersions,
    previewType: state.editorPreviewType,
    previewVersionCourseLanguage: state.editorPreviewVersionCourseLanguage,
    previewVersionCourseLanguageVersions:
      state.editorPreviewVersionCourseLanguageVersions,
    publishDate: state.editorPublishDate,
    status: state.editorHeaderStatus,
    translationReferenceLanguage: state.editorTranslationReferenceLanguage,
    updatedAt: state.editorHeaderUpdatedAt
  };

  const { theme } = useReduxState();

  const [visibleBlocksSignature, selectedBlocksSignature] = useMemo(() => {
    const visibleBlocks: number[] = [];
    const selectedBlocks: number[] = [];

    for (const id of state.editorBlocksOrder) {
      const block = state.editorBlocks[id];
      if (block?.data.visibility !== 'hidden') visibleBlocks.push(id);
      if (block?.isSelected) selectedBlocks.push(id);
    }

    return [JSON.stringify(visibleBlocks), JSON.stringify(selectedBlocks)];
  }, [state.editorBlocksOrder, state.editorBlocks]);

  const selectedBlockIds = useMemo<number[]>(() => {
    return JSON.parse(selectedBlocksSignature);
  }, [selectedBlocksSignature]);

  const actionIsHide = useMemo(() => {
    const visibleBlocks: number[] = JSON.parse(visibleBlocksSignature);
    return selectedBlockIds.some((id) => visibleBlocks.includes(id));
  }, [visibleBlocksSignature, selectedBlockIds]);

  return (
    <div className="header">
      <div className="header-content">
        <div className="header-inner">
          <div className="chapter">
            <EditorHeaderChapter
              activeLanguageVersion={stateProps.activeLanguageVersion}
              currentChapter={stateProps.currentChapter}
              isDraft={stateProps.isDraft}
              isPublishing={stateProps.isPublishing}
              languageVersions={stateProps.languageVersions}
              previewType={stateProps.previewType}
              previewVersionCourseLanguage={
                stateProps.previewVersionCourseLanguage
              }
              previewVersionCourseLanguageVersions={
                stateProps.previewVersionCourseLanguageVersions
              }
              publishDate={stateProps.publishDate}
              status={stateProps.status}
              translationProgressData={
                translationProgressData?.progressByLanguage
              }
              translationReferenceLanguage={
                stateProps.translationReferenceLanguage
              }
              updatedAt={stateProps.updatedAt}
            />
          </div>

          {!state.editorPreviewType && (
            <>
              <div className="actions">
                <EditorHeaderActions
                  activeLanguageVersion={stateProps.activeLanguageVersion}
                  chapters={stateProps.chapters}
                  currentChapter={stateProps.currentChapter}
                  importVisible={stateProps.importVisible}
                  isDisabled={stateProps.isDisabled}
                  isDiscarding={stateProps.isDiscarding}
                  isDraft={stateProps.isDraft}
                  isSaving={stateProps.isSaving}
                  updateTranslationProgress={updateTranslationProgressData}
                />
              </div>

              <EditorImport
                currentChapter={stateProps.currentChapter}
                importVisible={stateProps.importVisible}
                isImporting={stateProps.isImporting}
              />
            </>
          )}
        </div>

        {!state.editorActiveLanguageVersion && (
          <EditorHeaderSelectedBlocks
            actionIsHide={actionIsHide}
            chapters={stateProps.chapters}
            currentChapter={stateProps.currentChapter}
            isCopyingBlocks={stateProps.isCopyingBlocks}
            isCopyingBlocksSuccess={stateProps.isCopyingBlocksSuccess}
            isMoving={stateProps.isMoving}
            selectedBlockIds={selectedBlockIds}
          />
        )}
      </div>

      <style jsx>{`
        .header {
          background-color: ${theme.pageBackgroundColor};
          box-shadow: 0 -5px 0 3px ${theme.pageBackgroundColor};
          padding-top: 10px;
          position: sticky;
          top: 0;
          z-index: 15;
        }
        .header-content {
          background-color: white;
          border-radius: ${`${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0`};
          box-shadow: ${theme.cardBoxShadow};
        }
        .header-inner {
          padding: 15px 15px 12px;
          position: relative;
        }
        @media (max-width: ${breakpoints.large - 1}px) {
          .actions {
            margin-top: 15px;
            text-align: right;
          }
        }
        @media (min-width: ${breakpoints.large}px) {
          .actions {
            position: absolute;
            right: 15px;
            top: 15px;
          }
        }
      `}</style>
    </div>
  );
};

export default memo(EditorHeader);
